"use client";

import { Button, Field, Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { showErrorToast, showWarningToast } from "@/shared/feedback";
import type {
  MeiliOrigin,
  Origin,
  PostOriginRequest,
} from "@/generated/fastapi";
import useOriginActions from "../hooks/useOriginActions";
import appSettings from "@/shared/config/appSettings";
import { useCallback, useState, type BaseSyntheticEvent } from "react";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import type { OriginEntryMode, OriginSubmitIntent } from "../types";
import OriginAudioFileCreateFlow from "./OriginAudioFileCreateFlow";
import OriginVoiceCreateFlow from "./OriginVoiceCreateFlow";
import SourceMaterialPicker from "./SourceMaterialPicker";

interface OriginSubmitFormValues {
  originContent: string;
  userNote?: string;
}

interface OriginSubmitFormProps {
  onSuccess?: (origin: Origin, intent: OriginSubmitIntent) => void; // Callback when origin is successfully created/updated
  onError?: (error: unknown) => void; // Callback when error occurs
  submitButtonText?: string; // Customizable button text
  submitAndContinueButtonText?: string; // Optional second action for workflows that continue after create
  resetFormOnSuccess?: boolean; // Whether to reset form on success (default: true)
  sourcedWhere?: PostOriginRequest["sourced_where"]; // Defaults to backlog for generic shell usage
  showOrientation?: boolean;
  allowExistingSourceSelection?: boolean;
  onExistingSourceSelect?: (origin: MeiliOrigin) => void;
}

const ENTRY_MODE_LABELS: Record<OriginEntryMode, string> = {
  text: "Text input",
  voice: "Voice recording",
  audioFile: "Audio file",
  fromBacklog: "From backlog",
};

function formatSourceScope(value: PostOriginRequest["sourced_where"]) {
  return value.replace(/_/g, " ");
}

function getSubmitIntent(event?: BaseSyntheticEvent): OriginSubmitIntent {
  const submitter = (event?.nativeEvent as SubmitEvent | undefined)?.submitter;

  if (
    submitter instanceof HTMLButtonElement &&
    submitter.value === "add_and_extract"
  ) {
    return "add_and_extract";
  }

  return "add";
}

export default function OriginSubmitForm({
  onSuccess,
  onError,
  submitButtonText,
  submitAndContinueButtonText,
  resetFormOnSuccess,
  sourcedWhere = "backlog",
  showOrientation = true,
  allowExistingSourceSelection = false,
  onExistingSourceSelect,
}: OriginSubmitFormProps) {
  const [entryMode, setEntryMode] = useState<OriginEntryMode>("text");
  const [audioFlowResetKey, setAudioFlowResetKey] = useState(0);
  const [isInstantSubmitting, setIsInstantSubmitting] = useState(false);
  const normalizeOptionalNote = useCallback((value?: string | null) => {
    const normalized = value?.trim() ?? "";
    return normalized.length > 0 ? normalized : null;
  }, []);
  const {
    clearErrors,
    getValues,
    register,
    handleSubmit,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OriginSubmitFormValues>({
    defaultValues: {
      originContent: "",
      userNote: "",
    },
  });
  const { handleAddNewOriginClick } = useOriginActions();
  const isVoiceMode = entryMode === "voice";
  const isAudioFileMode = entryMode === "audioFile";
  const isFromBacklogMode = entryMode === "fromBacklog";
  const originContentValue = watch("originContent");
  const isTranscriptMode = isVoiceMode || isAudioFileMode;
  const cleanedOriginContent = originContentValue.trim();
  const isContentTooShort =
    !isFromBacklogMode &&
    cleanedOriginContent.length > 0 &&
    cleanedOriginContent.length < appSettings.validation.ORIGIN_MIN_LENGTH;
  const submitBlockedMessage = isFromBacklogMode
    ? "Choose source material from the backlog to shape it."
    : cleanedOriginContent.length === 0
      ? isTranscriptMode
        ? "Add a transcript before submitting."
        : "Add source material before submitting."
      : isContentTooShort
        ? `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters.`
        : null;
  const isSubmitDisabled =
    isSubmitting ||
    isInstantSubmitting ||
    isFromBacklogMode ||
    Boolean(submitBlockedMessage);
  const activeModeLabel = ENTRY_MODE_LABELS[entryMode];
  const canSelectExistingSource =
    allowExistingSourceSelection && Boolean(onExistingSourceSelect);

  const resetAudioDraftState = useCallback(() => {
    resetField("originContent", { defaultValue: "" });
    clearErrors("originContent");
    setAudioFlowResetKey((current) => current + 1);
  }, [clearErrors, resetField]);

  const handleEntryModeChange = useCallback(
    (nextMode: OriginEntryMode) => {
      setEntryMode(nextMode);
      resetAudioDraftState();
    },
    [resetAudioDraftState],
  );

  const submitOriginData = useCallback(
    async (
      data: OriginSubmitFormValues,
      submitIntent: OriginSubmitIntent,
    ): Promise<boolean> => {
      try {
        const shouldResetForm = resetFormOnSuccess ?? true;

        const handleSuccess = (origin: Origin) => {
          if (shouldResetForm) {
            reset();
            if (isVoiceMode || isAudioFileMode) {
              resetAudioDraftState();
            }
          }
          onSuccess?.(origin, submitIntent);
        };

        const requestData: PostOriginRequest = {
          raw_text_input: data.originContent,
          user_note: normalizeOptionalNote(data.userNote) ?? undefined,
          sourced_where: sourcedWhere,
        };

        const createdOrigin = await handleAddNewOriginClick(
          requestData,
          (origin) => {
            handleSuccess(origin);
          },
        );

        if (!createdOrigin) {
          throw new Error("Creation failed");
        }

        return true;
      } catch (error) {
        console.error("Error during submission:", error);
        showErrorToast("Failed to add origin");
        onError?.(error);
        return false;
      }
    },
    [
      handleAddNewOriginClick,
      isAudioFileMode,
      isVoiceMode,
      normalizeOptionalNote,
      onError,
      onSuccess,
      reset,
      resetAudioDraftState,
      resetFormOnSuccess,
      sourcedWhere,
    ],
  );

  const handleTranscriptSubmit = useCallback(
    async (nextTranscript: string) => {
      const trimmedTranscript = nextTranscript.trim();

      if (trimmedTranscript.length < appSettings.validation.ORIGIN_MIN_LENGTH) {
        showWarningToast(
          `Transcript must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters`,
        );
        return;
      }

      setIsInstantSubmitting(true);
      const submitted = await submitOriginData(
        {
          originContent: trimmedTranscript,
          userNote: getValues("userNote"),
        },
        "add",
      );

      if (!submitted) {
        setValue("originContent", trimmedTranscript, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setIsInstantSubmitting(false);
    },
    [getValues, setValue, submitOriginData],
  );

  const onSubmit = handleSubmit(async (data, event) => {
    await submitOriginData(data, getSubmitIntent(event));
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="4" align="stretch">
        {showOrientation ? (
          <GenericDrawerInfo
            description="Create source material that can be processed into suggested thoughts."
            items={[
              {
                label: isFromBacklogMode ? "Existing source" : "New origin",
                colorPalette: "blue",
              },
              { label: activeModeLabel, colorPalette: "purple" },
              {
                label: formatSourceScope(sourcedWhere),
                colorPalette: "gray",
              },
            ]}
          />
        ) : null}

        <Field.Root>
          <Field.Label>Input mode</Field.Label>
          <GenericSegment<OriginEntryMode>
            activeState={entryMode}
            setActiveState={handleEntryModeChange}
            options={[
              { value: "text", label: "Text" },
              { value: "voice", label: "Voice" },
              { value: "audioFile", label: "Audio file" },
              ...(canSelectExistingSource
                ? [{ value: "fromBacklog" as const, label: "From backlog" }]
                : []),
            ]}
            size="sm"
            disabled={isSubmitting || isInstantSubmitting}
          />
          <Field.HelperText>
            {canSelectExistingSource
              ? "Choose whether to add new source material or shape existing backlog material."
              : "Choose whether to type the source material yourself or record it first."}
          </Field.HelperText>
        </Field.Root>

        {isFromBacklogMode ? (
          <SourceMaterialPicker
            onSelect={(origin) => {
              onExistingSourceSelect?.(origin);
            }}
          />
        ) : null}

        {isVoiceMode && (
          <OriginVoiceCreateFlow
            key={`voice-${audioFlowResetKey}`}
            isSubmittingTranscript={isInstantSubmitting}
            readyMessage="Transcript received. You can edit it below before adding the source material."
            sourcedWhere={sourcedWhere}
            transcribeAndSubmitLabel="Transcribe & add"
            transcriptValue={originContentValue}
            onTranscriptChange={(nextTranscript) => {
              setValue("originContent", nextTranscript, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onTranscriptReset={() => {
              resetField("originContent", { defaultValue: "" });
              clearErrors("originContent");
            }}
            onTranscriptSubmit={handleTranscriptSubmit}
          />
        )}

        {isAudioFileMode && (
          <OriginAudioFileCreateFlow
            key={`audio-file-${audioFlowResetKey}`}
            isSubmittingTranscript={isInstantSubmitting}
            readyMessage="Transcript received. You can edit it below before adding the source material."
            sourcedWhere={sourcedWhere}
            transcribeAndSubmitLabel="Transcribe & add"
            transcriptValue={originContentValue}
            onTranscriptChange={(nextTranscript) => {
              setValue("originContent", nextTranscript, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onTranscriptReset={() => {
              resetField("originContent", { defaultValue: "" });
              clearErrors("originContent");
            }}
            onTranscriptSubmit={handleTranscriptSubmit}
          />
        )}

        {!isFromBacklogMode ? (
          <Field.Root invalid={!!errors.originContent}>
            <Field.Label>
              {isTranscriptMode ? "Transcript" : "Content"}
            </Field.Label>
            {submitBlockedMessage ? (
              <Text mb="2" fontSize="sm" color="moveon.text.muted">
                {submitBlockedMessage}
              </Text>
            ) : null}
            <Textarea
              autoresize
              maxH="3xs"
              minH="3xs"
              placeholder={
                isTranscriptMode
                  ? "Your transcribed recording will appear here for editing..."
                  : "Used to ... "
              }
              {...register("originContent", {
                required: "Must provide content to process",
                minLength: {
                  value: appSettings.validation.ORIGIN_MIN_LENGTH,
                  message: `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters`,
                },
              })}
            />
            <Field.HelperText>
              {isTranscriptMode
                ? "Review and edit the transcript before adding the origin"
                : "Text content that you want to process for your career profile"}
            </Field.HelperText>
            <Field.ErrorText>{errors.originContent?.message}</Field.ErrorText>
          </Field.Root>
        ) : null}

        {!isFromBacklogMode ? (
          <Field.Root>
            <Field.Label>Note (optional)</Field.Label>
            <Input
              placeholder="Something to note ... "
              {...register("userNote")}
            />
            <Field.HelperText>
              Note for your reference (not processed, optional)
            </Field.HelperText>
          </Field.Root>
        ) : null}

        {!isFromBacklogMode ? (
          <Stack gap="1" align="flex-end" width="full">
            <Stack
              direction={{ base: "column", sm: "row" }}
              gap="2"
              align="flex-end"
              justify="flex-end"
              width="full"
            >
              <Button
                type="submit"
                bg="moveon.primary"
                loading={isSubmitting || isInstantSubmitting}
                loadingText="Submitting"
                disabled={isSubmitDisabled}
                name="submitIntent"
                value="add"
                minW={{ base: "10", sm: "auto" }}
                width="auto"
              >
                <GenericIcon icon="add" />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  {submitButtonText || "Add"}
                </Text>
              </Button>
              {submitAndContinueButtonText ? (
                <Button
                  type="submit"
                  variant="outline"
                  loading={isSubmitting || isInstantSubmitting}
                  loadingText="Submitting"
                  disabled={isSubmitDisabled}
                  name="submitIntent"
                  value="add_and_extract"
                  minW={{ base: "10", sm: "auto" }}
                  width="auto"
                >
                  <GenericIcon icon="extract" />
                  <Text as="span" display={{ base: "none", sm: "inline" }}>
                    {submitAndContinueButtonText}
                  </Text>
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </form>
  );
}
